/**
 * SL-5 P2-1 — OBJECTS-section MLEADERSTYLE object emission for the DrawingModel
 * → DXF export.
 *
 * When an MLEADER references a NON-Standard leader style, its body group 340
 * (`style_handle`) must resolve to a REAL emitted MLEADERSTYLE object — otherwise
 * a save/reopen falls back to the built-in Standard and LOSES the chosen style.
 * This module owns the model→DXF style conversion (`drawingMLeaderStyleToDxf
 * MLeaderStyle`), the reference-driven object builder, the entity-340 stamper and
 * the ACAD_MLEADERSTYLE dictionary wiring. Extracted from `dxfBridge.export.ts`
 * to keep that orchestrator under the 2000-line ceiling; the public surface
 * (`drawingMLeaderStyleToDxfMLeaderStyle`) is re-exported from `dxfBridge.export`
 * so external import paths are unchanged.
 */
import type {
  DxfBlockRecordEntry,
  DxfDictionaryEntry,
  DxfEntity,
  DxfGroup,
  DxfLinetypeEntry,
  DxfMultileaderStyleEntry,
  DxfTextStyleEntry,
} from "@dxf-ai-editor/dxf-io";

import { makeHandleMinter } from "./dxfBridge.annotativeExport";
import { modelLeaderLineWeightToDxf171 } from "./dxfBridge.entityConversion";
import { createMissingTextStyleEntry } from "./dxfBridge.textStyleRecord";
import { multileaderStyleSchema } from "./model";
import type { DrawingDocument, MultileaderStyle } from "./model";

/**
 * #432 r13 (@codex R13-2/R13-5) — is this style the BUILT-IN default "Standard"
 * (which AutoCAD provides implicitly, so no OBJECTS record / dict child is needed)?
 * Returns true ONLY when the style is named Standard, carries NO `sourceHandle`
 * (not an imported real object) AND every property equals the schema default. A
 * CUSTOMIZED imported Standard (sourceHandle) OR an EDITED authored Standard
 * (differs from the default height/dogleg/arrowhead/refs) returns false → it MUST
 * be emitted as an object so its body-340 resolves + its refs (340-343) survive.
 * ONE predicate shared by the export ref-handle pre-pass (R13-2) + the object
 * builder (R13-5) so the two never disagree about which Standard to emit.
 */
export function isDefaultStandardStyle(style: MultileaderStyle): boolean {
  if (style.name.toLowerCase() !== "standard") return false;
  if (style.sourceHandle) return false; // imported real object → emit it
  const def = multileaderStyleSchema.parse({ name: style.name }) as Record<
    string,
    unknown
  >;
  const cur = style as unknown as Record<string, unknown>;
  // #432 r14 (@codex B1) — iterate the UNION of keys. Two schema fields are
  // `.optional()` with NO `.default()` — textRightAttachmentType (178) and
  // basePoint — so Zod OMITS them from `def`, and an `Object.keys(def)`-only loop
  // NEVER compared them: an authored-and-edited Standard whose only deviation was
  // a non-default 178 (or basePoint) was mis-classified as default → the builder
  // skipped it → NO MLEADERSTYLE object → the property was LOST on save/reopen. The
  // union compares any field present on either side (absent-from-def ⇒ def[k]
  // undefined ⇒ a set value differs ⇒ emit). A truly-default authored Standard has
  // neither optional key set on `cur`, so the union reduces to `def`'s keys and it
  // still returns true (still skipped — no dup, no spurious record). sourceHandle is
  // already handled by the early return above; the `continue` keeps it inert here.
  for (const k of new Set([...Object.keys(def), ...Object.keys(cur)])) {
    if (k === "name" || k === "sourceHandle") continue;
    if (JSON.stringify(cur[k]) !== JSON.stringify(def[k])) return false;
  }
  return true;
}

/**
 * SL-5 P2-1 — encode a model MLEADERSTYLE colour string for a RAW-int DXF colour
 * group (91 leader / 93 text / 94 block-content). The model carries the colour
 * either as a raw DXF value STRING (imported round-trip, e.g. "-1056964608") or
 * as a NAME ("ByBlock" / "ByLayer" — the schema defaults). The writer emits
 * these codes as integers, so a bare name would produce `91 ByBlock` → an ezdxf
 * `DXFStructureError` (int() on "ByBlock"). Map the two AutoCAD CMC constants
 * (ByBlock 0xC1000000, ByLayer 0xC0000000 — the ByBlock value is exactly what
 * the R2018 skeleton's Standard MLEADERSTYLE emits) and pass a numeric value
 * verbatim (imported); an unknown name is OMITTED (undefined) so ezdxf fills the
 * documented default rather than reading an invalid tag — never emit garbage.
 */
function mleaderStyleColorToDxf(color: string): string | undefined {
  const t = color.trim();
  if (t === "") return undefined;
  const lower = t.toLowerCase();
  if (lower === "byblock") return "-1056964608"; // 0xC1000000
  if (lower === "bylayer") return "-1073741824"; // 0xC0000000
  return Number.isFinite(Number(t)) ? t : undefined;
}

/**
 * SL-5 F1 (@codex round-3) — encode a model MLEADERSTYLE lineweight string for
 * DXF group 92 (`leader_lineweight`). VERIFIED against ezdxf 1.4.3
 * `acdb_mleader_style.leader_lineweight`: default = −2 (ByBlock); the lineweight
 * enum sentinels are ByLayer −1, ByBlock −2, Default −3 (ezdxf
 * `const.LINEWEIGHT_BYLAYER/BYBLOCK/DEFAULT`).
 *
 * Group 92 is the SAME lineweight enum as the MLEADER body group 171 (identical
 * sentinels + identical −2/ByBlock default), so the string→enum mapping is the
 * single domain truth `modelLeaderLineWeightToDxf171` ("0.25"→25, "25"→25,
 * ByLayer→−1, Default→−3, raw "−1"/"−2"/"−3" pass-through, ByBlock/""→null);
 * cloning the mm/enum parsing here would be a jscpd dup of one truth.
 *
 * @codex round-2 (superseded) elided group 92 for ByLayer −1 AND Default −3 too.
 * But omitting 92 makes a re-read fall back to the ezdxf default ByBlock (−2), so
 * a style that explicitly chose ByLayer (−1) or Default (−3) LOST that choice and
 * reopened as ByBlock. FIX: ELIDE only when the value equals the verified default
 * ByBlock (symbolic "ByBlock", the raw enum "−2", or the empty/absent form →
 * `modelLeaderLineWeightToDxf171` returns null); emit the explicit enum for EVERY
 * other value, INCLUDING ByLayer (`92 −1`) and Default (`92 −3`), so the choice
 * round-trips. (`modelLeaderLineWeightToDxf171` passes a raw "−2" THROUGH as −2 —
 * its group-171 compact-default only elides symbolic "ByBlock" — so the raw
 * ByBlock enum is elided here explicitly.)
 */
function mleaderStyleLineWeightToDxf(weight: string): string | undefined {
  const t = weight.trim();
  // The raw ByBlock enum "-2" is the verified acdb_mleader_style default → ELIDE
  // so a re-read fills it (the shared mapping would otherwise emit a redundant
  // `92 -2`). Symbolic "ByBlock"/"" already resolve to null → undefined below.
  if (t === "-2") return undefined;
  const code = modelLeaderLineWeightToDxf171(t);
  return code === null ? undefined : String(code);
}

/**
 * Inverse mapping for the writer. Produces a DxfMultileaderStyleEntry
 * from a DrawingModel MultileaderStyle. The handle field is left
 * empty — the caller assigns handles at serialization time.
 */
export function drawingMLeaderStyleToDxfMLeaderStyle(
  style: MultileaderStyle,
): DxfMultileaderStyleEntry {
  return {
    kind: "mleader-style",
    handle: "",
    name: style.name,
    contentType: style.contentType,
    textStyleName: style.textStyleName,
    textHeight: style.textHeight,
    // SL-5 P2-1 — 93/91/92/94 are RAW-int DXF codes: encode the model colour /
    // lineweight NAME or raw-value string so the writer never emits a bare
    // "ByBlock" into an int code (ezdxf DXFStructureError).
    textColor: mleaderStyleColorToDxf(style.textColor),
    textLeftAttachmentType: style.textAttachmentType,
    // SL-5 F1 (@codex round-6) — write group 178 (text_right_attachment_type) back
    // through the app export path (the DrawingModel bridge previously had no 178
    // field, so an imported non-default right attachment was DROPPED). Undefined
    // when the source carried no 178 → the writer omits it (no fabricated default).
    textRightAttachmentType: style.textRightAttachmentType,
    textAttachmentDirection: style.textAttachmentDirection,
    textFrameEnabled: style.textFrameDisplay,
    leaderType: style.leaderType,
    leaderLineType: style.leaderLineType,
    leaderLineColor: mleaderStyleColorToDxf(style.leaderColor),
    leaderLineWeight: mleaderStyleLineWeightToDxf(style.leaderWeight),
    enableLanding: style.landingEnabled,
    enableDogleg: style.doglegEnabled,
    // SL-5 P3b — CORRECTED: landing_gap_size is code 42 (landingDistance ←
    // style.landingGap), dogleg_length is code 43 (doglegLength ← style.dogleg
    // Length), align_space is code 46 (alignSpace ← style.alignSpace). The prior
    // mapping wrote doglegLength→42 and landingGap→46, dropping 43 entirely.
    landingDistance: style.landingGap,
    doglegLength: style.doglegLength,
    alignSpace: style.alignSpace,
    maxLeaderSegmentPointsNumber: style.maxLeaderSegments,
    arrowheadSize: style.arrowheadSize,
    arrowheadBlock: style.arrowheadBlock,
    blockContentName: style.blockName,
    blockContentScaleX: style.blockScale,
    blockContentColor: mleaderStyleColorToDxf(style.blockColor),
    blockContentConnectionType: style.blockConnectionType,
    blockContentRotation: style.blockRotation,
    enableAnnotationScale: style.enableAnnotation,
    scaleOverall: style.overallScale,
    breakGapSize: style.breakGapSize,
    extraGroups: { ...style.extraMlVars },
  };
}

/** Reserved linetype names that resolve to a built-in, never a real LTYPE ref. */
const RESERVED_LEADER_LINETYPE_NAMES = new Set(["byblock", "bylayer", "continuous"]);

/**
 * SL-5 F2 (@codex round-7) — guarantee a group-5 handle for EVERY table record a
 * referenced non-Standard MLEADERSTYLE points at, BEFORE the orchestrator builds
 * the name→handle resolution maps. A MLEADERSTYLE resolves FOUR references (ezdxf
 * 1.4.3 `acdb_mleader_style`):
 *   340 leader_linetype_handle ← `leaderLineType`  (LTYPE record)
 *   341 arrow_head_handle      ← `arrowheadBlock`  (BLOCK_RECORD)
 *   342 text_style_handle      ← `textStyleName`   (STYLE record)
 *   343 block_record_handle    ← `blockName`       (BLOCK_RECORD)
 *
 * SEED tables (the acad.lin linetypes, a from-scratch STYLE / BLOCK_RECORD) carry
 * NO handle, so `serialize*Entry` emits the record WITHOUT a group-5 and the
 * resolution map (built from `record.handle`) DROPS the reference → the style's
 * linetype / arrowhead / text style / content block silently falls back to the
 * default on reopen. Round-6 fixed ONLY the text-style leg; this stops the
 * per-ref-type treadmill by minting a handle for ANY referenced-but-handle-less
 * record so BOTH the emitted record AND the map carry the SAME handle (all four
 * 340-343 resolve). A referenced STYLE that does not yet exist is CREATED (a
 * MLEADERSTYLE 342 must point at a real STYLE record — mirrors the pre-existing
 * text-style stamp); a missing LTYPE / BLOCK_RECORD (or a ByBlock/ByLayer/
 * Continuous linetype sentinel) is left absent — its ref omits the handle (the
 * built-in resolves it, never a dangling handle). `emittedHandles` grows in place
 * so downstream minters (the MLEADERSTYLE-object mint, the $HANDSEED census) stay
 * collision-free.
 */
export function stampMLeaderStyleReferencedRecordHandles(
  referencedLeaderStyleNames: ReadonlySet<string>,
  leaderStyles: ReadonlyArray<MultileaderStyle>,
  textStyles: DxfTextStyleEntry[],
  linetypes: ReadonlyArray<DxfLinetypeEntry> | undefined,
  blockRecords: ReadonlyArray<DxfBlockRecordEntry>,
  emittedHandles: Set<string>,
  startSeq: number,
): void {
  if (referencedLeaderStyleNames.size === 0) return;
  const mint = makeHandleMinter(emittedHandles, startSeq);
  const stampHandle = (record: { handle?: string } | undefined): void => {
    if (!record || record.handle) return;
    record.handle = mint();
    emittedHandles.add(record.handle.toLowerCase());
  };
  const styleByLower = new Map<string, MultileaderStyle>();
  for (const s of leaderStyles) styleByLower.set(s.name.toLowerCase(), s);
  const textByLower = new Map<string, DxfTextStyleEntry>();
  for (const t of textStyles) textByLower.set(t.name.toLowerCase(), t);
  const linetypeByLower = new Map<string, DxfLinetypeEntry>();
  for (const lt of linetypes ?? []) linetypeByLower.set(lt.name.toLowerCase(), lt);
  const blockByLower = new Map<string, DxfBlockRecordEntry>();
  for (const b of blockRecords) blockByLower.set(b.name.toLowerCase(), b);

  for (const lower of referencedLeaderStyleNames) {
    const style = styleByLower.get(lower);
    if (!style) continue;
    // (342) text style — a 342 must resolve to a real STYLE record, so CREATE a
    // missing one (mirrors the pre-existing per-entity text-style stamp).
    const tsName = style.textStyleName?.trim();
    if (tsName && tsName.toLowerCase() !== "standard") {
      let rec = textByLower.get(tsName.toLowerCase());
      if (!rec) {
        // T9b review round 1 (P2) — the FULL canonical record with its table-
        // head owner (see `createMissingTextStyleEntry`), never a bare `{ name }`.
        rec = createMissingTextStyleEntry(tsName, textStyles);
        textStyles.push(rec);
        textByLower.set(tsName.toLowerCase(), rec);
      }
      stampHandle(rec);
    }
    // (340) leader linetype — mint for an EXISTING handle-less LTYPE record; a
    // reserved sentinel (ByBlock/ByLayer/Continuous) resolves to the built-in.
    const ltName = style.leaderLineType?.trim();
    if (ltName && !RESERVED_LEADER_LINETYPE_NAMES.has(ltName.toLowerCase())) {
      stampHandle(linetypeByLower.get(ltName.toLowerCase()));
    }
    // (341) arrowhead block + (343) content block — mint for existing records.
    const arrowName = style.arrowheadBlock?.trim();
    if (arrowName) stampHandle(blockByLower.get(arrowName.toLowerCase()));
    const blockName = style.blockName?.trim();
    if (blockName) stampHandle(blockByLower.get(blockName.toLowerCase()));
  }
}

/**
 * SL-5 P2-1 — build the OBJECTS-section MLEADERSTYLE objects for every
 * NON-STANDARD leader style an MLEADER entity actually references, minting one
 * collision-free handle per style so the entity's body 340 resolves to a REAL
 * emitted object (not a dangling synthetic handle → save/reopen would fall back
 * to Standard and LOSE the chosen style). The style properties come from the
 * document's typed `styleTables.multileaderStyles` (the same catalogue the import
 * promotes MLEADERSTYLE objects into) via `drawingMLeaderStyleToDxfMLeaderStyle`.
 *
 * "Standard" is DELIBERATELY excluded: it is AutoCAD's built-in MLEADERSTYLE, so
 * a Standard MLEADER's 340 (source-preserved or synthetic) resolves to the
 * built-in on reopen — no object emission needed, and NOT touching the Standard
 * path keeps every from-scratch/import round-trip (all Standard-only today)
 * byte-identical (the hard-won conformant-skeleton census is unaffected unless a
 * non-Standard style is genuinely used).
 *
 * A referenced style with NO entry in `styleTables` yields no object (it falls
 * back to the built-in path) — we never fabricate a style we don't have.
 *
 * `startSeq` is the collision-free mint floor the caller computes from the
 * document-wide emitted-handle maximum (kept in the orchestrator so the single
 * `maxHexHandleValue` definition is not duplicated here).
 *
 * SL-5 F2 (@codex round-2) — `refHandles` carries the document's emitted
 * TEXTSTYLE / LTYPE / BLOCK_RECORD name→handle maps so each style's NAME
 * references (textStyleName / leaderLineType / arrowheadBlock / blockName)
 * resolve to their real table-record handle fields (340-343). Without this the
 * writer emitted the object WITHOUT any 340-343, so a custom text style /
 * linetype / arrowhead / block content silently fell back to defaults on reopen.
 *
 * SL-5 F6 (@codex round-2) — an IMPORTED non-Standard style carries its source
 * MLEADERSTYLE handle on `MultileaderStyle.sourceHandle`; REUSE it (mint only for
 * genuinely-authored styles) so save→reopen→save is byte-stable for the object
 * handle + its dict child, instead of re-minting a fresh handle every cycle and
 * orphaning any raw reactor / dict child still pointing at the old handle.
 *
 * @returns the emitted entries + a lowercased-name → emitted-handle map so the
 * stamper resolves each entity's `styleName` to the SAME handle the object
 * carries (single source of truth — the mint is not independent of the object).
 */
export function buildReferencedMLeaderStyleObjects(
  document: DrawingDocument,
  entities: ReadonlyArray<DxfEntity>,
  dictHandle: string | undefined,
  seedHandles: ReadonlySet<string>,
  startSeq: number,
  // SL-5 F1 (@codex round-4) — group-5 handles of surviving OBJECTS records
  // (except MLEADERSTYLE objects) a reused source handle must NOT collide with.
  // Lowercased. See {@link collectForeignObjectRecordHandles}.
  foreignRecordHandles: ReadonlySet<string>,
  // SL-5 F3 (@codex round-6) — the document-wide OWN (group-5-class) handle set:
  // every emitted entity / table / block / OBJECTS record group-5, lowercased
  // (`collectOwnHandlesDeep`). SUPERSET of `foreignRecordHandles` (which covers
  // only OBJECTS records); it ALSO blocks a source handle that collides with a
  // TABLES / BLOCKS / ENTITIES own handle (overlapping handle spaces after an
  // insert/merge) — those are invisible to `foreignRecordHandles`, so the reuse
  // guard would otherwise emit two records sharing a group-5 → ambiguous 340. The
  // own-only walk EXCLUDES the source's self-references (dict 350 + entity 340), so
  // it never blocks the F6 byte-stable reuse of a genuinely-free source handle.
  documentOwnHandles: ReadonlySet<string>,
  refHandles?: {
    textStyleHandleByName: ReadonlyMap<string, string>;
    lineTypeHandleByName: ReadonlyMap<string, string>;
    blockHandleByName: ReadonlyMap<string, string>;
  },
): { entries: DxfMultileaderStyleEntry[]; handleByName: Map<string, string> } {
  const entries: DxfMultileaderStyleEntry[] = [];
  const handleByName = new Map<string, string>();
  // Index the typed catalogue by lowercased name (needed by the referenced-name
  // collection below to tell a BUILT-IN Standard from a CUSTOMIZED imported one).
  const styleByName = new Map<string, MultileaderStyle>();
  for (const s of document.styleTables?.multileaderStyles ?? []) {
    styleByName.set(s.name.toLowerCase(), s);
  }
  // Collect the leader-style names referenced by any MLEADER. A "Standard"
  // reference is SKIPPED ONLY when it is the BUILT-IN default (AutoCAD provides it,
  // no object needed). A CUSTOMIZED imported Standard (`sourceHandle`) OR an EDITED
  // authored Standard (differs from the default height/dogleg/arrowhead/refs) is
  // rebuilt (@codex r11/R13-5) — else its object + dict child are dropped and the
  // entity's body 340 dangles → the customized/edited Standard reopens as defaults.
  const referenced = new Set<string>();
  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "MLEADER") {
      continue;
    }
    const name = entity.geometry.styleName ?? "Standard";
    const lower = name.toLowerCase();
    if (lower === "standard") {
      const std = styleByName.get("standard");
      if (std === undefined || isDefaultStandardStyle(std)) {
        continue; // built-in default Standard — no object needed
      }
    }
    referenced.add(lower);
  }
  if (referenced.size === 0) return { entries, handleByName };
  // Mint in a band disjoint from the text-style (0x100000) / synthetic
  // leader-style (0x110000) stamps and below the annotative graph (0x200000);
  // the seed set (document-wide emitted handles) guarantees no collision anyway.
  const mint = makeHandleMinter(seedHandles, startSeq);
  // #432 r10 R10-4 — handles MINTED by `mint()` in THIS loop (distinct from the
  // minter's SEED, which legitimately already contains a style's OWN source handle
  // via the dict-350 child + entity body-340 ref). `mint.reserve`/`has` cannot
  // tell a seeded handle from a minted one, so an authored style that mints INTO
  // the band (e.g. `130001`) followed by an imported style whose `sourceHandle`
  // equals that mint would be reused → duplicate group-5. This set is the precise
  // collision signal: a source that THIS loop already minted must NOT be reused.
  const mintedThisLoop = new Set<string>();
  const mintTracked = (): string => {
    const h = mint();
    mintedThisLoop.add(h.toLowerCase());
    return h;
  };
  // SL-5 F4 — source handles ALREADY claimed by a PRIOR style in THIS build. This
  // is DISJOINT from the minter's seed set (which may legitimately already contain
  // a style's own source handle — e.g. a re-imported MLEADERSTYLE object): the
  // seed says "some handle exists", but only THIS set says "a sibling style
  // already re-used this exact source", which is the real collision to guard.
  const claimedSourceHandles = new Set<string>();
  // SL-5 F2 (@codex round-5) — belt-and-suspenders: a `sourceHandle` carried by
  // TWO+ referenced styles (a UI duplicate that inherited it before the
  // copy-site strip in `addMultileaderStyle`, or any path that clones it) may be
  // reused by NONE of them. The prior "first-claimant keeps it"
  // (`claimedSourceHandles`) let whichever name sorts FIRST HIJACK the original
  // object handle — the other style reminted, so a dictionary / reactor ref to
  // that handle pointed at the WRONG style after save/reopen. Count the
  // referenced-and-present styles per source handle; a count ≥2 forces ALL of
  // them to mint fresh (distinct handles, no hijack). The genuine SINGLE-owner
  // case is untouched — it still reuses (F6 byte-stability).
  const sourceHandleOwners = new Map<string, number>();
  for (const lower of referenced) {
    const src = styleByName.get(lower)?.sourceHandle?.trim().toLowerCase();
    if (src) sourceHandleOwners.set(src, (sourceHandleOwners.get(src) ?? 0) + 1);
  }
  const duplicatedSourceHandles = new Set<string>();
  for (const [h, n] of sourceHandleOwners) {
    if (n >= 2) duplicatedSourceHandles.add(h);
  }
  // Deterministic order (sorted) so repeated saves mint stable handles.
  for (const lower of [...referenced].sort()) {
    const style = styleByName.get(lower);
    if (!style) continue; // referenced but undefined — built-in fallback, no object
    const entry = drawingMLeaderStyleToDxfMLeaderStyle(style);
    // SL-5 F2 — resolve each NAME reference against its correct table so the
    // writer emits the 340-343 handle fields (self-consistent with the parser's
    // field↔code + resolution convention: textStyleName↔TEXTSTYLE↔textStyleHandle,
    // leaderLineType↔LTYPE↔leaderLineTypeHandle, arrowheadBlock/blockName↔
    // BLOCK_RECORD↔arrowheadBlockHandle/blockContentHandle). A name that does not
    // resolve (schema sentinels "ByBlock"/"" / an unknown record) leaves the
    // handle field UNDEFINED — the writer omits it (never a dangling handle).
    if (refHandles) {
      resolveMLeaderStyleReferenceHandles(entry, style, refHandles);
    }
    // SL-5 F6 — reuse the imported source handle so the object handle (and its
    // ACAD_MLEADERSTYLE dict child) is byte-stable across save→reopen→save. Mint
    // a fresh handle only for a genuinely-authored style (no source handle).
    //
    // SL-5 F4 (@codex round-3) — guard a DUPLICATED source handle. When the UI
    // duplicates an imported style, the style-copy path SPREADS the base style,
    // so the copy inherits the SAME `sourceHandle`. If BOTH the original and the
    // copy are referenced, the prior code re-used that one handle for BOTH emitted
    // MLEADERSTYLE objects → a duplicate object handle makes the entity `340`
    // ambiguous → one style lost on reopen (same class as the shipped SL-4
    // clone-inherits-handle bug). SL-5 F2 (@codex round-5) TIGHTENED this: a
    // sourceHandle shared by TWO+ referenced styles (`duplicatedSourceHandles`)
    // is reused by NONE — round-3 kept the first-claimant, but that first style
    // still HIJACKED the original object's handle away from whichever record a
    // dict / reactor ref points at, so ALL duplicates now mint fresh.
    // `claimedSourceHandles` remains the DISJOINT-from-minter-seed guard for the
    // residual single-claim path (the seed may already contain a style's own
    // source handle from the re-imported object, which must NOT block its
    // legitimate re-use, so `reserve`'s return value is NOT the collision
    // signal). Any duplicated / collided / authored style MINTS a fresh
    // collision-free handle; the reused handle is `reserve`d so `mint()` never
    // reproduces it.
    // SL-5 F1 (@codex round-4) — REUSE the imported source handle only when it
    // does NOT collide with a DIFFERENT surviving record's group-5 handle. The
    // minter seed legitimately CONTAINS a style's own source handle (via the dict
    // `350` child + the entity body `340` reference), so `mint.reserve`'s
    // seed-membership return is NOT the collision signal (that would wrongly
    // re-mint every imported style — F6 byte-stability). `foreignRecordHandles`
    // is the real guard: the group-5 handles of every surviving OBJECTS record
    // EXCEPT the MLEADERSTYLE objects being replaced (e.g. the ACAD_MLEADERSTYLE
    // DICTIONARY handle `F` in a conformant blank). Reusing a source equal to one
    // of THOSE would duplicate that record's handle (a dict child pointing at the
    // dict itself) → mint fresh. `duplicatedSourceHandles` blocks a shared source
    // up-front (all mint fresh); `claimedSourceHandles` is the residual sibling
    // guard; `mint.reserve` keeps a later mint() off a reused (genuinely-free
    // authored) handle.
    const source = style.sourceHandle?.trim();
    const sourceLower = source?.toLowerCase();
    if (
      source &&
      sourceLower &&
      !duplicatedSourceHandles.has(sourceLower) &&
      !claimedSourceHandles.has(sourceLower) &&
      !foreignRecordHandles.has(sourceLower) &&
      // SL-5 F3 (@codex round-6) — reuse only when the source is genuinely free
      // across the ENTIRE document's OWN handles (entity / table / block / OBJECTS
      // group-5), not just the OBJECTS-record subset. A collision here means a
      // different live record already owns this handle → mint fresh to avoid a
      // duplicate group-5 / ambiguous body 340.
      !documentOwnHandles.has(sourceLower) &&
      // #432 r10 R10-4 — a source equal to a handle THIS loop already MINTED (an
      // authored style that sorted first minted it INTO the band) is a COLLISION:
      // reusing it would emit TWO MLEADERSTYLE objects with the same group-5 →
      // strict readers reject. Mint a fresh handle instead (the remap propagates
      // via `handleByName` + `entry.handle` to the entity 340 and the dict child).
      // NOTE: unlike `mint.reserve`'s return, this does NOT fire for the style's
      // OWN seeded source handle (that is in the SEED, not minted this loop), so
      // the F6 byte-stable reuse of a genuinely-free imported handle is preserved.
      !mintedThisLoop.has(sourceLower)
    ) {
      claimedSourceHandles.add(sourceLower);
      mint.reserve(source); // keep a LATER mint() off this reused handle
      entry.handle = source;
    } else {
      entry.handle = mintTracked();
    }
    if (dictHandle) entry.ownerHandle = dictHandle;
    entries.push(entry);
    handleByName.set(lower, entry.handle);
  }
  return { entries, handleByName };
}

/**
 * SL-5 F2 — populate a MLEADERSTYLE entry's handle fields (340-343) from the
 * model style's NAME references, resolved against the document's emitted table
 * name→handle maps. Only assigns a field when the name resolves to a real
 * record — an unresolved name (default sentinel / unknown record) is left
 * undefined so the writer omits the group (no dangling handle). The field↔table
 * pairing mirrors `resolveMultileaderStyleHandles` (the import-side resolver),
 * so a written handle re-resolves back to the SAME name on reopen.
 */
function resolveMLeaderStyleReferenceHandles(
  entry: DxfMultileaderStyleEntry,
  style: MultileaderStyle,
  refHandles: {
    textStyleHandleByName: ReadonlyMap<string, string>;
    lineTypeHandleByName: ReadonlyMap<string, string>;
    blockHandleByName: ReadonlyMap<string, string>;
  },
): void {
  const resolve = (
    name: string | undefined,
    map: ReadonlyMap<string, string>,
  ): string | undefined => {
    const key = name?.trim().toLowerCase();
    if (!key) return undefined;
    return map.get(key);
  };
  const textStyle = resolve(style.textStyleName, refHandles.textStyleHandleByName);
  if (textStyle) entry.textStyleHandle = textStyle;
  // T9b — the reserved sentinels (ByBlock / ByLayer / Continuous) emit NO 340:
  // ezdxf's own `Standard` MLEADERSTYLE carries no `leader_linetype_handle`
  // (`ezdxf.new("R2018").mleader_styles.get("Standard")` → unset), the reader
  // resolves the built-in by default. Before T9b the sentinel LTYPE records had
  // no handle so the lookup missed by accident; now that every LTYPE record is
  // minted a group-5 the exclusion must be explicit.
  const ltKey = style.leaderLineType?.trim().toLowerCase();
  const lineType =
    ltKey && !RESERVED_LEADER_LINETYPE_NAMES.has(ltKey)
      ? resolve(style.leaderLineType, refHandles.lineTypeHandleByName)
      : undefined;
  if (lineType) entry.leaderLineTypeHandle = lineType;
  const arrowhead = resolve(style.arrowheadBlock, refHandles.blockHandleByName);
  if (arrowhead) entry.arrowheadBlockHandle = arrowhead;
  const blockContent = resolve(style.blockName, refHandles.blockHandleByName);
  if (blockContent) entry.blockContentHandle = blockContent;
}

/**
 * SL-5 P2 — stamp MLEADER entity LEADER-STYLE handles (AcDbMLeader body group
 * 340 = `style_handle`, the MLEADERSTYLE record handle).
 *
 * The MLEADER's leader style round-trips by NAME on the model
 * (`MultileaderEntity.styleName`); AutoCAD persists it as a HANDLE. ezdxf
 * `acdb_mleader.export_entity` writes `340 dxf.style_handle` (mandatory) right
 * after CONTEXT_DATA.
 *
 * Resolution rule (styleName is the single source of truth):
 *   • NON-Standard style WITH an emitted MLEADERSTYLE object
 *     (`objectHandleByName`) — point the 340 at that object's handle for EVERY
 *     entity carrying the name. For an AUTHORED entity this resolves the
 *     previously-dangling handle (P2-1); for an IMPORTED entity whose styleName
 *     was EDITED to this style the stale source 340 is REPLACED so the writer
 *     emits the NEW style (P2-3 — the source handle is preserved only while the
 *     name is unchanged, i.e. still resolves to no re-emitted object).
 *   • "Standard" / a style with no emitted object — keep the source handle
 *     verbatim for an IMPORTED entity (built-in Standard resolves it), else mint
 *     a synthetic hex (AUTHORED) so ezdxf still reads a valid 340 (ezdxf 1.4.3
 *     `readfile`+`audit` accept a dangling Standard handle: 0 errors / 0 fixes —
 *     Standard is a built-in, so nothing is lost). SL-5 F1 (@codex round-5) — the
 *     preserved source handle is kept ONLY while it still matches the CURRENT
 *     styleName binding. When the entity's styleName was EDITED away from a
 *     custom style that is STILL emitted (another MLEADER references it), the old
 *     source 340 points at that surviving custom object → a reopen resolves the
 *     edited entity back to the OLD style and LOSES the edit. Such a STALE handle
 *     (it equals one of the emitted MLEADERSTYLE object handles) is RE-RESOLVED
 *     to a fresh synthetic hex (the correct no-custom-object binding for Standard
 *     / any style with no emitted object), never preserved.
 */
export function stampMLeaderLeaderStyleHandles(
  entities: DxfEntity[],
  objectHandleByName: ReadonlyMap<string, string>,
  // #432 r11 (@codex) — a MUTABLE seed: every synthetic 340 minted below is folded
  // BACK into it so a LATER document minter (annotative graph / synthesized dict)
  // never reuses the same real handle (which would make the entity's otherwise-
  // dangling Standard 340 resolve to an unrelated object).
  seedHandles?: Set<string>,
): void {
  const syntheticByName = new Map<string, string>();
  const usedHandles = new Set<string>(seedHandles ?? []);
  // SL-5 F1 (@codex round-5) — the lowercased set of EVERY emitted MLEADERSTYLE
  // object handle. A preserved source 340 equal to one of THESE, while the
  // entity's CURRENT styleName has NO emitted object (the else-branch below), is
  // STALE: it still points at a DIFFERENT (custom) style's object that survives
  // because a sibling MLEADER references it. Preserving it would resolve the
  // edited entity back to the OLD style on reopen — the styleName edit (e.g.
  // custom→Standard) is LOST. Such a handle must be RE-RESOLVED, not preserved.
  const emittedObjectHandles = new Set<string>();
  for (const h of objectHandleByName.values()) {
    emittedObjectHandles.add(h.toLowerCase());
  }
  let handleSeq = 0x110000; // disjoint band from the text-style stamp (0x100000)
  const nextSyntheticHandle = (): string => {
    let candidate: string;
    do {
      handleSeq += 1;
      candidate = handleSeq.toString(16).toUpperCase();
    } while (usedHandles.has(candidate.toLowerCase()));
    usedHandles.add(candidate.toLowerCase());
    // Fold into the SHARED emitted-handle seed (usedHandles is a local COPY), so a
    // later OBJECTS minter that seeds from it cannot reproduce this real handle.
    seedHandles?.add(candidate.toLowerCase());
    return candidate;
  };

  for (const entity of entities) {
    if (entity.kind !== "supported-entity" || entity.type !== "MLEADER") {
      continue;
    }
    const name = (entity.geometry.styleName ?? "Standard").toLowerCase();
    // NON-Standard style with an emitted object → styleName is authoritative:
    // point the 340 at the object (resolves authored + edited-import handles).
    const objectHandle = objectHandleByName.get(name);
    if (objectHandle) {
      entity.geometry.leaderStyleHandle = objectHandle;
      continue;
    }
    // Standard / no-object for the CURRENT styleName: preserve an imported
    // source handle verbatim (built-in Standard resolves it) UNLESS it is STALE
    // — i.e. it still resolves to a DIFFERENT style's emitted MLEADERSTYLE
    // object. That is the custom→Standard (or custom→other-name) edit case where
    // the old custom object survives for a sibling MLEADER: keeping the old 340
    // would lose the edit on reopen. A stale handle falls through to the
    // synthetic mint (dangles to built-in Standard — lossless, ezdxf 0/0).
    const preserved = entity.geometry.leaderStyleHandle;
    if (preserved && !emittedObjectHandles.has(preserved.toLowerCase())) {
      continue;
    }
    // Authored (no handle) OR a STALE custom-object handle → mint a synthetic hex
    // (dangles to built-in Standard — lossless).
    let handle = syntheticByName.get(name);
    if (!handle) {
      handle = nextSyntheticHandle();
      syntheticByName.set(name, handle);
    }
    entity.geometry.leaderStyleHandle = handle;
  }
}

/**
 * SL-5 P2-1 — locate the handle of a NAMED entry (e.g. "ACAD_MLEADERSTYLE") in a
 * raw OBJECTS group stream's root Named-Object-Dictionary. The entry is a
 * `3 <name>` group immediately followed by a `350|360 <handle>` child. Returns
 * undefined when the stream carries no such entry (e.g. a minimal DXF with no
 * OBJECTS dictionaries). The name is unique to the root NOD, so a first-match
 * scan is unambiguous.
 */
/**
 * SL-5 F1 (@codex round-4) — the lowercased group-5 handles of every SURVIVING
 * OBJECTS-section record EXCEPT `0 MLEADERSTYLE` objects.
 *
 * A rebuilt style may legitimately REUSE its own imported source handle for
 * byte-stability (F6). That handle appears in the document only as a REFERENCE
 * (the dictionary `350` child + the entity body `340`), never as a surviving
 * record group-5 — the dxf-io parser consumes MLEADERSTYLE objects into the typed
 * model, so no raw `0 MLEADERSTYLE` record rides in the passthrough. A source
 * handle that instead collides with a DIFFERENT surviving record's group-5 (e.g.
 * the ACAD_MLEADERSTYLE DICTIONARY handle in a conformant blank) would DUPLICATE
 * that record's handle, so the builder must mint fresh for it. MLEADERSTYLE
 * records are excluded so a stray raw style object never blocks its own reuse.
 */
export function collectForeignObjectRecordHandles(
  rawGroups: ReadonlyArray<{ code: number; value: string }>,
): Set<string> {
  const out = new Set<string>();
  let currentType: string | undefined;
  for (const g of rawGroups) {
    if (g.code === 0) {
      currentType = g.value.trim();
      continue;
    }
    if (g.code === 5 && currentType !== "MLEADERSTYLE") {
      const h = g.value.trim();
      if (h) out.add(h.toLowerCase());
    }
  }
  return out;
}

export function findObjectDictionaryHandle(
  rawGroups: ReadonlyArray<{ code: number; value: string }>,
  entryName: string,
): string | undefined {
  for (let i = 0; i + 1 < rawGroups.length; i += 1) {
    const g = rawGroups[i];
    if (g.code === 3 && g.value.trim() === entryName) {
      const next = rawGroups[i + 1];
      if (next && (next.code === 350 || next.code === 360)) {
        return next.value.trim();
      }
    }
  }
  return undefined;
}

/**
 * SL-5 P2-1 — REPLACE the `3 <name>` / `350|360 <handle>` child list of the
 * DICTIONARY record whose own handle (group 5) is `dictHandle` with EXACTLY the
 * supplied entries, keeping every other group (0/5/330/280/281/100) verbatim.
 * Mirrors the conformant-skeleton `patchLayoutDictionaryEntries` helper. Used to
 * register the freshly-emitted MLEADERSTYLE objects under the ACAD_MLEADERSTYLE
 * dictionary so each dict child resolves to a real object (and any stale
 * imported child pointing at a dropped source handle is removed). Pure — returns
 * a new array; the input is returned unchanged when the dictionary is not found.
 */
export function replaceDictionaryChildEntries(
  groups: ReadonlyArray<{ code: number; value: string }>,
  dictHandle: string,
  entries: ReadonlyArray<{ name: string; handle: string }>,
): { code: number; value: string }[] {
  const recordStarts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].code === 0) recordStarts.push(i);
  }
  for (let r = 0; r < recordStarts.length; r += 1) {
    const start = recordStarts[r];
    const end =
      r + 1 < recordStarts.length ? recordStarts[r + 1] : groups.length;
    const typeValue = groups[start].value;
    if (typeValue !== "DICTIONARY" && typeValue !== "ACDBDICTIONARYWDFLT") {
      continue;
    }
    let recHandle: string | undefined;
    for (let i = start + 1; i < end; i += 1) {
      if (groups[i].code === 5) {
        recHandle = groups[i].value.trim();
        break;
      }
    }
    if (recHandle?.toLowerCase() !== dictHandle.toLowerCase()) continue;

    // SL-5 F3 (@codex round-2) — drop ONLY the real dictionary children (the
    // depth-0 `3 <name>` / `350|360 <handle>` pairs being replaced), NEVER a
    // handle inside a nested `102 {...102 }` block. An imported dictionary can
    // carry `102 {ACAD_XDICTIONARY 360 <h> 102 }` (its extension-dictionary
    // pointer) and/or `102 {ACAD_REACTORS 330 <h> 102 }`; those `360`/`330` are
    // pointers, not child entries. The prior unconditional `350|360` drop deleted
    // the xdictionary `360`, leaving an empty xdict block + orphaning the dict's
    // extension data. Track `102` nesting depth and preserve every group inside a
    // block verbatim; only depth-0 child groups are dropped.
    const head: { code: number; value: string }[] = [];
    let insertAfter = -1;
    let depth = 0;
    for (let i = start; i < end; i += 1) {
      const g = groups[i];
      if (g.code === 102) {
        const tok = g.value.trim();
        if (tok.startsWith("{")) depth += 1;
        else if (tok === "}" && depth > 0) depth -= 1;
        head.push({ code: g.code, value: g.value }); // preserve the 102 marker
        continue;
      }
      if (depth === 0) {
        if (g.code === 3) continue; // drop existing depth-0 child name
        if (g.code === 350 || g.code === 360) continue; // drop existing depth-0 child handle
      }
      head.push({ code: g.code, value: g.value });
      if (depth === 0 && g.code === 100 && g.value.trim() === "AcDbDictionary") {
        insertAfter = head.length;
      }
    }
    const childGroups: { code: number; value: string }[] = [];
    for (const e of entries) {
      childGroups.push({ code: 3, value: e.name });
      childGroups.push({ code: 350, value: e.handle });
    }
    const at = insertAfter >= 0 ? insertAfter : head.length;
    const rebuilt = [...head.slice(0, at), ...childGroups, ...head.slice(at)];
    return [
      ...groups.slice(0, start).map((g) => ({ code: g.code, value: g.value })),
      ...rebuilt,
      ...groups.slice(end).map((g) => ({ code: g.code, value: g.value })),
    ];
  }
  return groups.map((g) => ({ code: g.code, value: g.value }));
}

/**
 * #432 r10 R10-3 — the handle of the raw OBJECTS ROOT DICTIONARY (a `0 DICTIONARY`
 * / `0 ACDBDICTIONARYWDFLT` record whose owner soft pointer group 330 is `"0"`),
 * or undefined when none. The OBJECTS section MUST bootstrap from a root dictionary
 * (ezdxf `ObjectsSection.rootdict = entity_space[0]`; a strict readfile raises
 * `invalid root dictionary` when the first record is not a DICTIONARY). Drives the
 * R10-3 (no root → synthesize one) vs R11-3 (root exists → add a child under it)
 * branch.
 */
function findRootObjectDictionaryHandle(
  groups: readonly DxfGroup[],
): string | undefined {
  for (let i = 0; i < groups.length; i += 1) {
    const g = groups[i]!;
    if (
      g.code === 0 &&
      (g.value === "DICTIONARY" || g.value === "ACDBDICTIONARYWDFLT")
    ) {
      let handle: string | undefined;
      let ownedByRoot = false;
      for (let j = i + 1; j < groups.length; j += 1) {
        const b = groups[j]!;
        if (b.code === 0) break;
        if (b.code === 5 && handle === undefined) handle = b.value.trim();
        if (b.code === 330 && b.value.trim() === "0") ownedByRoot = true;
      }
      if (ownedByRoot && handle !== undefined) return handle;
    }
  }
  return undefined;
}

/** Build the typed `ACAD_MLEADERSTYLE` sub-dictionary: `subHandle`, owned by
 * `ownerHandle`, listing every MLEADERSTYLE object by name→handle. One-truth
 * sub-dict shape shared by R10-3 (owner = a fresh root) and R11-3 (owner = the
 * existing root). MUTATES each entry's `ownerHandle` to `subHandle`. */
function buildMLeaderStyleSubDict(
  entries: DxfMultileaderStyleEntry[],
  subHandle: string,
  ownerHandle: string,
): DxfDictionaryEntry {
  for (const e of entries) e.ownerHandle = subHandle;
  return {
    kind: "dictionary",
    handle: subHandle,
    ownerHandle,
    hardOwned: false,
    cloning: 1,
    entries: entries.map((e) => ({ name: e.name, handle: e.handle, isHard: false })),
  };
}

/** Insert a `3 <name>` / `350 <handle>` child into an EXISTING dictionary record
 * (after its `100 AcDbDictionary` marker), preserving every other group verbatim —
 * unlike `replaceDictionaryChildEntries` it never re-emits an existing `360`
 * hard-owned child as `350`. Used to wire an `ACAD_MLEADERSTYLE` child into the
 * imported root dict for R11-3. */
function addDictionaryChildEntry(
  groups: readonly DxfGroup[],
  dictHandle: string,
  name: string,
  handle: string,
): DxfGroup[] {
  const starts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) if (groups[i]!.code === 0) starts.push(i);
  for (let r = 0; r < starts.length; r += 1) {
    const start = starts[r]!;
    const end = r + 1 < starts.length ? starts[r + 1]! : groups.length;
    const tv = groups[start]!.value;
    if (tv !== "DICTIONARY" && tv !== "ACDBDICTIONARYWDFLT") continue;
    let recHandle: string | undefined;
    for (let i = start + 1; i < end; i += 1) {
      if (groups[i]!.code === 5) { recHandle = groups[i]!.value.trim(); break; }
    }
    if (recHandle?.toLowerCase() !== dictHandle.toLowerCase()) continue;
    let insertAt = end;
    let depth = 0;
    for (let i = start; i < end; i += 1) {
      const g = groups[i]!;
      if (g.code === 102) {
        const t = g.value.trim();
        if (t.startsWith("{")) depth += 1;
        else if (t === "}" && depth > 0) depth -= 1;
        continue;
      }
      const v = g.value.trim();
      if (depth === 0 && g.code === 100 &&
        (v === "AcDbDictionary" || v === "AcDbDictionaryWithDefault")) {
        insertAt = i + 1;
        break;
      }
    }
    return [
      ...groups.slice(0, insertAt),
      { code: 3, value: name },
      { code: 350, value: handle },
      ...groups.slice(insertAt),
    ];
  }
  return groups.slice();
}

/**
 * #432 r10 R10-3 + r11 R11-3 — gate + synthesize the OBJECTS-section MLEADERSTYLE
 * dictionary chain for the standalone-authoring export path (`ensureConformant:
 * false` + no imported ACAD_MLEADERSTYLE dict). Returns the typed `dictionaries`
 * to emit AND the (possibly rewritten) `rawObjectGroups`. Three cases:
 *   - no MLEADERSTYLE objects OR an ACAD_MLEADERSTYLE dict already exists → no-op.
 *   - NO root dict (R10-3) → synthesize a fresh root + ACAD_MLEADERSTYLE sub-dict
 *     so the section does not lead with a bare `0 MLEADERSTYLE` (invalid root).
 *   - root dict EXISTS but has no ACAD_MLEADERSTYLE child (R11-3) → synthesize the
 *     sub-dict UNDER the existing root and add its `ACAD_MLEADERSTYLE` child to the
 *     root, so the objects are owned + reachable (no orphaned MLEADERSTYLE).
 * MUTATES each entry's `ownerHandle`. One entry point so the gating + synthesis
 * live in one place (and export.ts stays under its line ceiling).
 */
export function maybeSynthesizeMLeaderStyleRootDicts(
  entries: DxfMultileaderStyleEntry[],
  mleaderStyleDictHandle: string | undefined,
  objectsRawGroups: readonly DxfGroup[],
  emittedHandles: Set<string>,
  startSeq: number,
): { dictionaries: DxfDictionaryEntry[]; rawObjectGroups: DxfGroup[] } {
  const rawOut = objectsRawGroups.map((g) => ({ code: g.code, value: g.value }));
  if (entries.length === 0 || mleaderStyleDictHandle) {
    return { dictionaries: [], rawObjectGroups: rawOut };
  }
  const mint = makeHandleMinter(emittedHandles, startSeq);
  const existingRoot = findRootObjectDictionaryHandle(objectsRawGroups);
  if (existingRoot === undefined) {
    // R10-3 — no root: synthesize a fresh root + sub-dict.
    const rootHandle = mint();
    emittedHandles.add(rootHandle.toLowerCase());
    const subHandle = mint();
    emittedHandles.add(subHandle.toLowerCase());
    const rootDict: DxfDictionaryEntry = {
      kind: "dictionary",
      handle: rootHandle,
      ownerHandle: "0",
      hardOwned: false,
      cloning: 1,
      entries: [{ name: "ACAD_MLEADERSTYLE", handle: subHandle, isHard: false }],
    };
    const subDict = buildMLeaderStyleSubDict(entries, subHandle, rootHandle);
    return { dictionaries: [rootDict, subDict], rawObjectGroups: rawOut };
  }
  // R11-3 — root exists but no ACAD_MLEADERSTYLE dict: synthesize the sub-dict
  // under the existing root + wire an ACAD_MLEADERSTYLE child into that root.
  const subHandle = mint();
  emittedHandles.add(subHandle.toLowerCase());
  const subDict = buildMLeaderStyleSubDict(entries, subHandle, existingRoot);
  const rewritten = addDictionaryChildEntry(
    rawOut,
    existingRoot,
    "ACAD_MLEADERSTYLE",
    subHandle,
  );
  return { dictionaries: [subDict], rawObjectGroups: rewritten };
}
